// "Which version is effective TODAY" — the §3.4 answer, printed on the version
// screens (spec 01 §9.16, D-261).
//
// WHY A SEPARATE LINE. The invisibility of a future-dated activation was the
// whole defect: the version the planner was looking at said "Active", and the
// version production and costing were actually using was somewhere else in
// the switcher, unnamed. This line names it, from the server's own resolver
// (`/products/:id/{boms,routings}/effective`, "the one function everyone
// calls") rather than a client re-implementation of §3.4's facility-then-group
// fallback — two resolvers would eventually disagree, and the one the roll-up
// uses is the one that matters.
//
// Four states, none of them silent:
//   · pending   — a status line, not a blank;
//   · resolved  — "Effective today: version N", with Open when it is not this
//                 one; "This version is effective today" when it is;
//   · none      — the 404 (NO_EFFECTIVE_BOM / NO_EFFECTIVE_ROUTING) is a FACT
//                 to act on (nothing will plan or cost until a version is
//                 activated), rendered as an amber banner, never as an error;
//   · error     — anything else, with Retry and the request id.

import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/button";
import { Banner } from "../../../../components/toast";
import { RequestError, requestIdOf } from "../../../../lib/request";

export type EffectiveTodayState =
  | { kind: "pending" }
  | { kind: "resolved"; versionId: string; version: number }
  | { kind: "none" }
  | { kind: "error"; message: string | undefined; requestId: string | undefined };

/** Derive the note's state from a react-query result over the resolver. A 404
 * is the resolver's "no version covers this date" — not a failure. */
export function effectiveTodayStateOf(query: {
  isPending: boolean;
  error: unknown;
  data: { id: string; version: number } | undefined;
}): EffectiveTodayState {
  if (query.error !== null && query.error !== undefined) {
    if (query.error instanceof RequestError && query.error.httpStatus === 404) {
      return { kind: "none" };
    }
    return {
      kind: "error",
      message: query.error instanceof Error ? query.error.message : undefined,
      requestId: requestIdOf(query.error),
    };
  }
  if (query.isPending || query.data === undefined) return { kind: "pending" };
  return { kind: "resolved", versionId: query.data.id, version: query.data.version };
}

export function EffectiveTodayNote({
  today,
  state,
  currentVersionId,
  onOpen,
  onRetry,
}: {
  /** The business date the resolver was asked about (facility-local, D-089). */
  today: string;
  state: EffectiveTodayState;
  /** The version the screen is showing, so "this one" can be said. */
  currentVersionId: string;
  onOpen?: ((versionId: string) => void) | undefined;
  onRetry?: (() => void) | undefined;
}) {
  const { t } = useTranslation();

  if (state.kind === "pending") {
    return (
      <p
        className="effective-today"
        role="status"
        data-testid="effective-today"
        data-effective="pending"
      >
        {t("catalogue.effectiveToday.loading")}
      </p>
    );
  }

  if (state.kind === "none") {
    return (
      <Banner kind="warn" data-testid="effective-today" data-effective="none">
        {t("catalogue.effectiveToday.none", { date: today })}{" "}
        {t("catalogue.effectiveToday.noneHint")}
      </Banner>
    );
  }

  if (state.kind === "error") {
    return (
      <Banner
        kind="bad"
        data-testid="effective-today"
        data-effective="error"
        aside={
          onRetry !== undefined ? (
            <Button size="sm" onClick={onRetry}>
              {t("catalogue.state.retry")}
            </Button>
          ) : undefined
        }
      >
        {t("catalogue.effectiveToday.error")}
        {state.message !== undefined && state.message.length > 0 ? ` ${state.message}` : null}
        {state.requestId !== undefined ? (
          <span className="rte-request-id">
            {" "}
            {t("catalogue.state.requestId")}: <code>{state.requestId}</code>
          </span>
        ) : null}
      </Banner>
    );
  }

  const isThis = state.versionId === currentVersionId;
  return (
    <p
      className="effective-today"
      role="status"
      data-testid="effective-today"
      data-effective={isThis ? "this" : "other"}
      data-effective-version={String(state.version)}
    >
      {isThis
        ? t("catalogue.effectiveToday.thisVersion", { date: today })
        : t("catalogue.effectiveToday.resolved", { date: today, version: state.version })}
      {!isThis && onOpen !== undefined ? (
        <>
          {" "}
          <Button
            size="sm"
            onClick={() => {
              onOpen(state.versionId);
            }}
          >
            {t("catalogue.effectiveToday.open", { version: state.version })}
          </Button>
        </>
      ) : null}
    </p>
  );
}
